import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { CopropertyDto, UpdateCopropertyComplementsCommand } from "api/coproperty";
import { InputText } from "components/forms/InputText";
import { InputWithUnit } from "components/forms/fragments/InputWithUnit";
import { toDayInput } from "core/utils/dates";
import { parseDecimal } from "./CopropertyFieldControls";
import { CopropertySection } from "./CopropertyStepLayout";

interface CopropertyComplementsFormProps {
  coproperty: CopropertyDto;
  /**
   * Expose au bouton d'étape de la page la commande à enregistrer, ou null si la date de la
   * prochaine AG est invalide (le formulaire affiche alors l'erreur).
   */
  registerBuildCommand: (build: () => UpdateCopropertyComplementsCommand | null) => void;
}

/** Jour du navigateur au format d'un <input type="date">, décalé de `offsetDays` jours. */
const dayInput = (offsetDays = 0) => {
  const date = new Date();
  date.setDate(date.getDate() + offsetDays);
  return toDayInput(date);
};

/** La date prévisionnelle de la prochaine AG, si renseignée, doit être postérieure à aujourd'hui. */
const validateNextGeneralMeetingDate = (value: string): string | null =>
  value && value <= dayInput() ? "La date doit être postérieure à aujourd'hui." : null;

const toInput = (value: number | null | undefined) => (value != null ? String(value) : "");
/**
 * Le client généré sérialise les Date du command via toISOString() : minuit UTC
 * garantit que le jour calendaire envoyé est exactement celui de l'input.
 */
const fromDateInput = (value: string): Date | null => (value ? new Date(`${value}T00:00:00Z`) : null);

/**
 * Compléments de la copropriété (écrans 14-15 de la spec) : le contrat en cours — prime
 * HT, année d'exercice, cinq franchises — et la date prévisionnelle de la prochaine AG.
 * Tout est facultatif : la sous-étape est passable, rien n'est éliminatoire.
 */
export const CopropertyComplementsForm: FC<CopropertyComplementsFormProps> = ({ coproperty, registerBuildCommand }) => {
  const complements = coproperty.complements;

  const [premium, setPremium] = useState(toInput(complements?.currentPremiumExclTax));
  const [premiumYear, setPremiumYear] = useState(toInput(complements?.premiumYear));
  const [deductibleGeneral, setDeductibleGeneral] = useState(toInput(complements?.deductibleGeneral));
  const [deductibleFire, setDeductibleFire] = useState(toInput(complements?.deductibleFire));
  const [deductibleTheft, setDeductibleTheft] = useState(toInput(complements?.deductibleTheft));
  const [deductibleWaterDamage, setDeductibleWaterDamage] = useState(toInput(complements?.deductibleWaterDamage));
  const [deductibleVandalism, setDeductibleVandalism] = useState(toInput(complements?.deductibleVandalism));
  const [nextGeneralMeetingDate, setNextGeneralMeetingDate] = useState(toDayInput(complements?.nextGeneralMeetingDate));
  const [showErrors, setShowErrors] = useState(false);
  const [meetingDateTouched, setMeetingDateTouched] = useState(false);

  const meetingDateError = useMemo(
    () => validateNextGeneralMeetingDate(nextGeneralMeetingDate),
    [nextGeneralMeetingDate]
  );

  const command = useMemo<UpdateCopropertyComplementsCommand>(
    () => ({
      currentPremiumExclTax: parseDecimal(premium),
      premiumYear: premiumYear.trim() ? Number(premiumYear) : null,
      deductibleGeneral: parseDecimal(deductibleGeneral),
      deductibleFire: parseDecimal(deductibleFire),
      deductibleTheft: parseDecimal(deductibleTheft),
      deductibleWaterDamage: parseDecimal(deductibleWaterDamage),
      deductibleVandalism: parseDecimal(deductibleVandalism),
      nextGeneralMeetingDate: fromDateInput(nextGeneralMeetingDate),
    }),
    [
      premium,
      premiumYear,
      deductibleGeneral,
      deductibleFire,
      deductibleTheft,
      deductibleWaterDamage,
      deductibleVandalism,
      nextGeneralMeetingDate,
    ]
  );

  const buildCommand = useCallback((): UpdateCopropertyComplementsCommand | null => {
    if (meetingDateError) {
      setShowErrors(true);
      return null;
    }
    return command;
  }, [command, meetingDateError]);
  useEffect(() => {
    registerBuildCommand(buildCommand);
  }, [registerBuildCommand, buildCommand]);

  return (
    <>
      <CopropertySection title="Contrat en cours">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <InputWithUnit
            label="Première prime (HT)"
            name="currentPremiumExclTax"
            unit="€"
            type="number"
            min={0}
            value={premium}
            onChange={(e) => setPremium(e.target.value)}
          />
          <InputText
            label="Année d'exercice"
            name="premiumYear"
            type="number"
            min={1900}
            max={2100}
            placeholder="2026"
            value={premiumYear}
            onChange={(e) => setPremiumYear(e.target.value)}
          />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <InputWithUnit
            label="Franchise générale"
            name="deductibleGeneral"
            unit="€"
            type="number"
            min={0}
            value={deductibleGeneral}
            onChange={(e) => setDeductibleGeneral(e.target.value)}
          />
          <InputWithUnit
            label="Franchise incendie"
            name="deductibleFire"
            unit="€"
            type="number"
            min={0}
            value={deductibleFire}
            onChange={(e) => setDeductibleFire(e.target.value)}
          />
          <InputWithUnit
            label="Franchise vol"
            name="deductibleTheft"
            unit="€"
            type="number"
            min={0}
            value={deductibleTheft}
            onChange={(e) => setDeductibleTheft(e.target.value)}
          />
          <InputWithUnit
            label="Franchise dégât des eaux"
            name="deductibleWaterDamage"
            unit="€"
            type="number"
            min={0}
            value={deductibleWaterDamage}
            onChange={(e) => setDeductibleWaterDamage(e.target.value)}
          />
          <InputWithUnit
            label="Franchise vandalisme"
            name="deductibleVandalism"
            unit="€"
            type="number"
            min={0}
            value={deductibleVandalism}
            onChange={(e) => setDeductibleVandalism(e.target.value)}
          />
        </div>
      </CopropertySection>

      <CopropertySection title="Prochaine assemblée générale">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <InputText
            label="Date prévisionnelle"
            name="nextGeneralMeetingDate"
            type="date"
            min={dayInput(1)}
            value={nextGeneralMeetingDate}
            error={(showErrors || meetingDateTouched) && meetingDateError ? meetingDateError : undefined}
            onChange={(e) => {
              setNextGeneralMeetingDate(e.target.value);
              setMeetingDateTouched(true);
            }}
          />
        </div>
        <p className="text-[13px] text-gray-500">
          Sert au suivi commercial du dossier, sans effet sur l'éligibilité ni sur le tarif.
        </p>
      </CopropertySection>
    </>
  );
};
